/**
 * Attachment Attribution Chip — spec 19
 *
 * The drafter marks any claim sourced from a file the user attached to the
 * conversation with `[ATT: filename]`. That token is authoring metadata, not
 * prose: it must never reach an export or a word count, and on screen it must
 * never show as raw text.
 *
 * Rendered as a quiet, non-interactive chip — there is nothing to open, because
 * a conversation attachment is not an indexed document with a citation target.
 *
 * Shared because two independent chat surfaces render the same token: the
 * assistant panel (`@/components/assistant/MessageBubble`) and the drafting
 * panel's own local bubble (`@/components/drafting/UnifiedAiPanel`). Both are
 * fed by orchestrators that inject the guidance teaching `[ATT:]`, so a chip
 * that lives in only one of them is a leak in the other.
 */

import { Paperclip } from "lucide-react"

/**
 * The label is whatever the token carried, verbatim. The prompt binds the
 * FILENAME, not the token payload, so the model may append a qualifier
 * (`[ATT: site-notes.pdf, p.4]`); guessing a filename out of that would be
 * wrong more often than it would be right.
 */
export function AttAttributionChip({ filename }: { filename: string }) {
  const label = filename.trim() || 'attached document'
  return (
    <span
      // `title` is a mouse affordance only — it is not announced — so the chip
      // carries an explicit label. Without it a screen reader hears the
      // filename with no indication of where the claim came from.
      role="img"
      aria-label={`From your attached document: ${label}`}
      title="From your attached document"
      className="inline-flex items-center gap-0.5 rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground align-baseline"
    >
      <Paperclip className="h-2.5 w-2.5 shrink-0" />
      {label}
    </span>
  )
}

/**
 * react-markdown component-map entry for the `att-attribution` node emitted by
 * `remarkAttAttributions`. Register it unconditionally — the remark plugin runs
 * on every message, and an unmapped custom element renders as an empty tag,
 * which would swallow the filename entirely rather than show it.
 */
export function AttAttributionNodeComponent({ filename }: { filename?: string }) {
  return <AttAttributionChip filename={filename ?? ''} />
}
